/**
 * `.WLD` placement `x`/`y` unit scale — **VERIFIED**, documented in full in
 * docs/formats/world-terrain.md's placement-scale section.
 *
 * A `.WLD` placement coordinate is one **eighth of a foot**, i.e. the
 * original sim's own world unit — *not* the 65-units-per-metre unit its
 * models' vertices use. Two independent measurements against the running
 * `RB.EXE` under DOSBox-X settle it (`re-sessions/terrain/`, 2026-08-19):
 *
 * 1. **The engine stores `.WLD` `x`/`y` verbatim in its runtime world-object
 *    table.** Searching a 640 KB dump of a live Verdun flight for each
 *    placement's `(x, y)` `int32` pair finds 31 of the 380 unrescaled, in a
 *    32-byte-stride array (linear `0x56ac6` onward — a 32-byte runtime
 *    record, not the 20-byte file image). Searching for the same pairs scaled
 *    by 65/26.2467 or its reciprocal finds **zero**. The engine does not
 *    convert placement coordinates on load, so they are already world units.
 * 2. **The world unit is 1/8 foot.** Independently calibrated by the flight
 *    lane from the player record's own position field against the airspeed
 *    indicator (`re-sessions/calib/NOTES.md` §2: 1114 units/s = 95 mph).
 *
 * Corroboration: at 1/8 ft, the five regions' placement bounding boxes come
 * out 33-66 miles across, which sits sensibly inside the manual's 80×80-mile
 * regional maps; at 65 units/metre they would be 12-27 miles, leaving two
 * thirds of every map with no scenery in it at all.
 *
 * **This is a different unit from the one a `.TBL` model's own vertices use**
 * (65 units/metre, VERIFIED in models-tbl.md §4 against real wingspans — a
 * `FOKD7` measures 8.88 m at that scale against a real 8.90 m). The original
 * therefore scales model geometry by 8/20 = 0.4 when it places it, the two
 * units being 1/8 foot for the world and 1/20 foot for a model
 * (65.617 units/metre, within 1% of the measured 65 and a far more plausible
 * authored constant). Nothing here changes model geometry — `packages/baker`
 * keeps converting vertices at 65 — this constant only converts *placement*
 * coordinates, which is exactly where the two units were previously conflated.
 */

/** 8 units per foot; `0.3048` m per foot. */
export const WLD_UNITS_PER_METRE = 8 / 0.3048;

export function wldUnitsToMetres(units: number): number {
  return units / WLD_UNITS_PER_METRE;
}
